import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../shared/redux/store';
import { fetchData, LocalitiesStore } from '../../components/Localities/localitiesSlice';
import { RequestContext, useRequestContext } from './Request.Context';
import { fetchSourceInstitutions, fetchSourceSyllabusActivities, fetchSourceSyllabuses, fetchTargetSyllabuses, saveRequest } from './requestSlice';
import Institution, { InstitutionSearchParams } from '../../models/Institution';
import Syllabus, { SuggestedSyllabus, SyllabusSearchParams } from '../../models/Syllabus';
import Activity, { ActivitySearchParams } from '../../models/Activity';
import EquivalencyRequest from '../../models/EquivalencyRequest';
import { Simulation } from '../../models/Simulation';
import { StudentRecordsSyllabus } from '../../models/StudentRecord';

export interface RequestData extends RequestContext {
  optionssourceInstitutions: Institution[],
  optionssourceSyllabuses: Syllabus[],
  optionsTargetSyllabuses: Syllabus[],
  optionsSourceActivities: Activity[],
  targetInstitution: Institution | null
  simulation: Simulation | null,
  createdRequest: EquivalencyRequest | null,
  localities: LocalitiesStore,
  activities: Activity[],
  allReadySaved: boolean
  onFetchSourceInstitutions: (params: InstitutionSearchParams, signal?: AbortSignal) => void,
  onFetchSourceSyllabus: (params: SyllabusSearchParams) => void,
  onFetchTargetSyllabus: (params: SyllabusSearchParams) => void,
  saveEquivalencyRequest: () => void,
  onFetchLocalities: () => void,
  rejectedUserProfile: boolean,
  compatibleTargetSyllabuses: SuggestedSyllabus[] | null,
  studentRecordsSyllabuses: StudentRecordsSyllabus[] | null,
  contactId: string | null,
  onFetchSourceActivities: (params: ActivitySearchParams) => void,
  dealId: string
}

interface RequestDataConfig {
  handleSuccess?: () => void
}
export const useRequestdata = ({ handleSuccess }: RequestDataConfig): RequestData => {
  const dispatch = useAppDispatch();
  const optionssourceInstitutions = useAppSelector(state => state.request.sourceInstitutions);
  const optionssourceSyllabuses = useAppSelector(state => state.request.sourceSyllabuses);
  const optionsTargetSyllabuses = useAppSelector(state => state.request.targetSyllabuses);
  const compatibleTargetSyllabuses = useAppSelector(state => state.request.compatibilityTargetSyllabuses);
  const studentRecordsSyllabuses = useAppSelector(state => state.request.studentRecordsSyllabuses);
  const contactId = useAppSelector(state => state.request.contactId);
  const dealId = useAppSelector(state => state.request.dealId);
  const targetInstitution = useAppSelector(state => state.request.targetInstitution);
  const optionsSourceActivities = useAppSelector(state => state.request.sourceActivities);
  const studentSelected = useAppSelector(state => state.request.student);
  const createdRequest = useAppSelector(state => state.request.selected);
  const simulation = useAppSelector(state => state.request.simulation);
  const localities = useAppSelector(state => state.localities);
  const rejectedUserProfile = useAppSelector(state => state.user?.rejectedUserProfile);
  const requestContext = useRequestContext();
  const { allReadySaved, activities, setStudent, source_institution: sourceInstitution } = requestContext;

  useEffect(() => {
    if (createdRequest) {
      handleSuccess?.();
    }
  }, [createdRequest]);

  useEffect(() => {
    if (studentSelected) {
      setStudent(studentSelected);
    }
  }, [studentSelected]);

  // on autocomplete changes
  const onFetchSourceInstitutions = (params: InstitutionSearchParams, signal?: AbortSignal) => {
    dispatch(fetchSourceInstitutions({parameters: params, signal})).catch(e => console.log(e));
  };

  const onFetchSourceSyllabus = (params: SyllabusSearchParams) => {
    if (sourceInstitution?.id) {
      const parameters = { ...params, institutionId: sourceInstitution.id };
      dispatch(fetchSourceSyllabuses(parameters)).catch(e => console.log(e));
    }
  };

  const onFetchTargetSyllabus = (params: SyllabusSearchParams) => {
    if (targetInstitution?.id) {
      const parameters = { ...params, institutionId: targetInstitution.id, canBeTarget: true };
      dispatch(fetchTargetSyllabuses(parameters)).catch(e => console.log(e));
    }
  };

  const onFetchLocalities = () => {
    dispatch(fetchData()).catch(e => console.log(e));
  };

  const onFetchSourceActivities = (params: ActivitySearchParams) => {
    dispatch(fetchSourceSyllabusActivities(params))
  }
  // apicall
  const saveEquivalencyRequest = () => {
    requestContext.saveStudentRequest((request: EquivalencyRequest) =>
      dispatch(saveRequest({ request }))
    );
  };

  return {
    ...requestContext,
    optionssourceInstitutions,
    optionssourceSyllabuses,
    optionsTargetSyllabuses,
    optionsSourceActivities,
    simulation,
    createdRequest,
    localities,
    activities,
    targetInstitution,
    onFetchSourceInstitutions,
    onFetchSourceSyllabus,
    onFetchTargetSyllabus,
    saveEquivalencyRequest,
    onFetchLocalities,
    rejectedUserProfile,
    allReadySaved,
    compatibleTargetSyllabuses,
    studentRecordsSyllabuses,
    contactId,
    dealId,
    onFetchSourceActivities
  };
};
